import styled from '@emotion/styled'

import { FileUploadType } from '@bemlo/enums'
import { truncate } from '@bemlo/utils'

import { Color, theme } from '../../../constants'
import { Clickable, Paper, Text } from '../../../elements'
import { useTranslations } from '../../../translate'
import { EditIcon } from '../components/EditIcon'
import { CandidateViewDocument } from '../types'

const ClickableArea = styled(Clickable)<{ isPlaceholder: boolean }>(
  (props) => ({
    borderRadius: theme.border.radius.normal,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: 4,
    height: '100%',
    padding: 8,
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: props.isPlaceholder
        ? Color.LIGHTEST_BLUE
        : Color.LIGHTER_BLUE,
    },
  }),
)

const FileUploadElement = styled(Paper)<{ isPlaceholder: boolean }>(
  (props) => ({
    display: 'flex',
    gap: 4,
    borderColor: Color.TRANSPARENT_BLUE,
    borderStyle: props.isPlaceholder ? 'dashed' : undefined,
    borderWidth: props.isPlaceholder ? theme.border.width.thick : undefined,
    boxShadow: props.isPlaceholder ? 'none' : undefined,
    padding: 4,
    transition: theme.animation.transition,
    userSelect: 'none',
  }),
)

export type FileUploadProps = {
  fileUpload: CandidateViewDocument | null
  onAddClick: (uploadType: FileUploadType) => void
  onEditClick: (fileUpload: CandidateViewDocument) => void
  onOpenClick: (fileUpload: CandidateViewDocument) => void
  uploadType: FileUploadType
}

export const FileUpload = ({
  fileUpload,
  onAddClick,
  onEditClick,
  onOpenClick,
  uploadType,
}: FileUploadProps) => {
  const t = useTranslations()

  const isPlaceholder = !fileUpload
  const isFileUploadPlaceholder =
    isPlaceholder && uploadType === FileUploadType.OTHER

  return (
    <FileUploadElement
      design={isPlaceholder ? 'white' : 'blue'}
      isPlaceholder={isPlaceholder}
    >
      <ClickableArea
        isPlaceholder={isPlaceholder}
        onClick={
          fileUpload
            ? () => onOpenClick(fileUpload)
            : () => onAddClick(uploadType)
        }
      >
        {!isFileUploadPlaceholder && (
          <Text
            bold
            color={fileUpload ? Color.DARKEST_BLUE : Color.BLUE}
            size={6}
            noWrap
          >
            {fileUpload?.comment
              ? truncate(fileUpload.comment, 40)
              : t.$fileUploadType(uploadType)}
          </Text>
        )}
        <Text
          color={fileUpload ? Color.DARK_BLUE : Color.LIGHT_BLUE}
          size={6.5}
          noWrap
        >
          {fileUpload?.fetchedAt
            ? t.candidate_view.file_uploads.fetched_at(
                t.$date.unix(fileUpload.fetchedAt).format('ll'),
              )
            : fileUpload?.uploadedAt
              ? t.candidate_view.file_uploads.uploaded_at(
                  t.$date.unix(fileUpload.uploadedAt).format('ll'),
                )
              : fileUpload
                ? null
                : isFileUploadPlaceholder
                  ? t.candidate_view.file_uploads.upload_document()
                  : t.candidate_view.file_uploads.upload()}
        </Text>
      </ClickableArea>
      {!isPlaceholder && (
        <EditIcon
          onClick={() => onEditClick(fileUpload)}
          permissionScope="fileUploads"
        />
      )}
    </FileUploadElement>
  )
}
